import React, { useEffect, useState } from "react";
import { useStateDraft } from "@/hooks/useStateDraft";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { FileText, Upload } from "lucide-react";
import { updateUserProfile } from "@/services/edgeFunctionWrappers/profilesManagementWrapper";
import { Textarea } from "@/components/ui/textarea";

interface ProfileMedicalFormProps {
  userId?: string;
  profileData: any;
}

export const ProfileMedicalForm: React.FC<ProfileMedicalFormProps> = ({
  userId,
  profileData
}) => {
  const { t } = useTranslation(["profile", "common"]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [currentFile, setCurrentFile] = useState<string | null>(
    profileData?.medical_form_url || null
  );

  const [medicalForm, setMedicalForm] = useState({
    hasPathologies: profileData?.has_pathologies ?? false,
    pathologies: profileData?.pathologies || "",
    hasTreatment: profileData?.has_treatment ?? false,
    treatment: profileData?.treatment || "",
    hasAllergies: profileData?.has_allergies ?? false,
    allergies: profileData?.allergies || "",
    hasContraindications: profileData?.has_contraindications ?? false,
    contraindications: profileData?.contraindications || "",
  });

  // Brouillon persisté (4 zones de texte libres — la saisie la plus coûteuse
  // à perdre du profil). Restauration silencieuse à la remontée de l'onglet.
  const { hasRestoredDraft, clearDraft } = useStateDraft(
    `profile-medical:${userId}`,
    medicalForm,
    setMedicalForm,
    {
      isEmpty: (d) =>
        !d.pathologies && !d.treatment && !d.allergies && !d.contraindications &&
        !d.hasPathologies && !d.hasTreatment && !d.hasAllergies && !d.hasContraindications,
    }
  );

    useEffect(() => {
      // Un brouillon restauré prime sur la re-synchronisation serveur.
      if (hasRestoredDraft) return;
      if (profileData) {
        setMedicalForm({
          hasPathologies: profileData?.has_pathologies ?? false,
          pathologies: profileData?.pathologies || "",
          hasTreatment: profileData?.has_treatment ?? false,
          treatment: profileData?.treatment || "",
          hasAllergies: profileData?.has_allergies ?? false,
          allergies: profileData?.allergies || "",
          hasContraindications: profileData?.has_contraindications ?? false,
          contraindications: profileData?.contraindications || "",
        });
      }
    }, [profileData, hasRestoredDraft]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile || !userId) {
      toast.error(t("errors.noFileSelected", { ns: "profile" }));
      return;
    }

    try {
      setIsUploading(true);
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${userId}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${userId}/${fileName}`;

      const { error: uploadError } = await supabase
        .storage
        .from('medical_forms')
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('medical_forms').getPublicUrl(filePath);

      // updateUserProfile renvoie un booléen, pas un { error }.
      const profileOk = await updateUserProfile(userId, {
        medical_form_url: data.publicUrl,
        medical_form_name: selectedFile.name,
      });

      if (!profileOk) throw new Error('PROFILE_UPDATE_FAILED');

      setCurrentFile(data.publicUrl);
      setSelectedFile(null);
      toast.success(t("success.fileUploaded", { ns: "profile" }));
    } catch (error) {
      console.error("Error uploading medical form:", error);
      toast.error(t("errors.fileUploadFailed", { ns: "profile" }));
    } finally {
      setIsUploading(false);
    }
  };

  const saveMedicalData = async () => {
    if (!userId) return;
    try {
      const ok = await updateUserProfile(userId, {
        has_pathologies: medicalForm.hasPathologies,
        pathologies: medicalForm.pathologies,
        has_treatment: medicalForm.hasTreatment,
        treatment: medicalForm.treatment,
        has_allergies: medicalForm.hasAllergies,
        allergies: medicalForm.allergies,
        has_contraindications: medicalForm.hasContraindications,
        contraindications: medicalForm.contraindications,
      });
      if (ok) {
        clearDraft();
        toast.success(t("success.updated", { ns: "profile" }));
      } else {
        toast.error(t("errors.saveFailed", { ns: "profile" }));
      }
    } catch (err) {
      toast.error(t("errors.saveFailed", { ns: "profile" }));
    }
  };

  return (
    <Card className="border shadow-sm">
      <CardHeader>
        <CardTitle>{t("medical.title", { ns: "profile" })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pathologies */}
        <div className="space-y-2">
          <Label>{t("medical.pathologiesKnown", { ns: "profile" })}</Label>
          <Checkbox
            checked={medicalForm.hasPathologies}
            onCheckedChange={(checked) =>
              setMedicalForm((prev) => ({ ...prev, hasPathologies: checked as boolean }))
            }
          />
          {medicalForm.hasPathologies && (
            <Textarea
              placeholder={t("medical.pathologiesDetails", { ns: "profile" })}
              value={medicalForm.pathologies}
              onChange={(e) =>
                setMedicalForm((prev) => ({ ...prev, pathologies: e.target.value }))
              }
            />
          )}
        </div>

        {/* Traitement en cours */}
        <div className="space-y-2">
          <Label>{t("medical.treatmentInProgress", { ns: "profile" })}</Label>
          <Checkbox
            checked={medicalForm.hasTreatment}
            onCheckedChange={(checked) =>
              setMedicalForm((prev) => ({ ...prev, hasTreatment: checked as boolean }))
            }
          />
          {medicalForm.hasTreatment && (
            <Textarea
              placeholder={t("medical.treatmentDetails", { ns: "profile" })}
              value={medicalForm.treatment}
              onChange={(e) =>
                setMedicalForm((prev) => ({ ...prev, treatment: e.target.value }))
              }
            />
          )}
        </div>

        {/* Allergies */}
        <div className="space-y-2">
          <Label>{t("medical.allergies", { ns: "profile" })}</Label>
          <Checkbox
            checked={medicalForm.hasAllergies}
            onCheckedChange={(checked) =>
              setMedicalForm((prev) => ({ ...prev, hasAllergies: checked as boolean }))
            }
          />
          {medicalForm.hasAllergies && (
            <Textarea
              placeholder={t("medical.allergyDetails", { ns: "profile" })}
              value={medicalForm.allergies}
              onChange={(e) =>
                setMedicalForm((prev) => ({ ...prev, allergies: e.target.value }))
              }
            />
          )}
        </div>

        {/* Contre-indications */}
        <div className="space-y-2">
          <Label>{t("medical.contraindications", { ns: "profile" })}</Label>
          <Checkbox
            checked={medicalForm.hasContraindications}
            onCheckedChange={(checked) =>
              setMedicalForm((prev) => ({ ...prev, hasContraindications: checked as boolean }))
            }
          />
          {medicalForm.hasContraindications && (
            <Textarea
              placeholder={t("medical.contraindicationDetails", { ns: "profile" })}
              value={medicalForm.contraindications}
              onChange={(e) =>
                setMedicalForm((prev) => ({ ...prev, contraindications: e.target.value }))
              }
            />
          )}
        </div>

        

        {/* Upload Fichier médical */}
        <div className="mt-6">
          {currentFile && (
            <div className="flex items-center gap-4 p-4 bg-secondary/50 rounded-md">
              <FileText className="h-8 w-8 text-foreground/70" />
              <div className="flex-1">
                <p className="font-medium">
                  {profileData?.medical_form_name || t("medical.currentForm", { ns: "profile" })}
                </p>
                <p className="text-sm text-muted-foreground">{t("medical.uploaded")}</p>
              </div>
              <Button variant="outline" onClick={() => window.open(currentFile, "_blank")}>
                {t("buttons.view", { ns: "profile" })}
              </Button>
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <input
              id="medical-file"
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={handleFileChange}
            />
            <Button variant="outline" onClick={() => document.getElementById("medical-file")?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              {t("buttons.selectFile", { ns: "profile" })}
            </Button>
            <Button onClick={uploadFile} disabled={!selectedFile || isUploading}>
              {isUploading ? t("uploading", { ns: "common" }) : t("upload", { ns: "common" })}
            </Button>
          </div>
          {selectedFile && (
            <p className="text-sm text-muted-foreground mt-2">
              {t("medical.selectedFile", { ns: "profile" })}: {selectedFile.name}
            </p>
          )}
        </div>
        <Button onClick={saveMedicalData}>
          {t("save", { ns: "common" })}
        </Button>
      </CardContent>
    </Card>
  );
};
